import { useHistory } from 'react-router-dom';
import AppPaths from '../AppPaths';
import { getFiGrowthBook, useIsGrowthBookEnabled } from './growthbook';
import { DEFAULT_QUIZ_VARIANT, LEGACY_VARIANT, parseQuizVariantOverride } from './fiQuizOverride';

// Persists a querystring override so it survives client-side navigation for the
// rest of the session (matches how the email popup keeps its force state).
const QUIZ_VARIANT_STORAGE_KEY = 'fi_quiz_variant_override';

declare global {
  interface Window {
    // Exposed by the embedded fi-quizz widget (quiz.fitracking.com/widget.js).
    FiQuiz?: {
      open: (source?: string) => void;
      close: () => void;
    };
    // Host contract: the widget fails closed unless we set `enabled: true`.
    // `variant` names the design the widget should render (see fi-quizz).
    FiQuizConfig?: {
      enabled?: boolean;
      variant?: string;
    };
  }
}

/**
 * Reads the quiz-variant override from the current URL, persisting it to
 * sessionStorage so it survives SPA navigation, and falling back to any
 * previously-stored value. Safe to call when the URL / sessionStorage are
 * unavailable (privacy mode, non-browser env).
 */
function getStickyQuizVariantOverride(): string | undefined {
  try {
    const fromUrl = parseQuizVariantOverride(window.location.search);
    if (fromUrl) {
      window.sessionStorage?.setItem(QUIZ_VARIANT_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return window.sessionStorage?.getItem(QUIZ_VARIANT_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Returns a handler that launches the product-recommendation quiz.
 *
 * A `?quizVariant=` (or `?forceShowQuiz`) override wins over the flag so either
 * quiz — and any named widget design variant — can be exercised in prod before
 * Ultra GA. `?quizVariant=legacy` forces the legacy quiz; any other override
 * value forces the widget with that design variant. Otherwise, when the
 * `ultra-experience` flag is on (i.e. Fi Ultra is GA) and the embedded fi-quizz
 * widget has loaded, it opens the new widget (default variant) — which
 * recommends Mini / Series 3+ / Ultra. In every other case it falls back to the
 * legacy in-app quiz at `AppPaths.Quiz`, so there is always a working quiz.
 */
export function useLaunchQuiz(): () => void {
  const history = useHistory();
  const growthBookReady = useIsGrowthBookEnabled();

  return () => {
    const override = getStickyQuizVariantOverride();
    const flagEnabled = growthBookReady && getFiGrowthBook().getFeatureValue<boolean>('ultra-experience', false);

    // 'legacy' forces the old quiz; any other override forces the widget with
    // that design variant; with no override the flag decides (default variant).
    const openWidget = override ? override !== LEGACY_VARIANT : flagEnabled;
    const variant = override && override !== LEGACY_VARIANT ? override : DEFAULT_QUIZ_VARIANT;

    if (openWidget && window.FiQuiz) {
      // Affirm the widget's fails-closed contract and name the design to render.
      window.FiQuizConfig = { enabled: true, variant };
      window.FiQuiz.open('ecom_plp');
      return;
    }

    history.push(AppPaths.Quiz);
  };
}
